import { useCallback } from "react";
import { AssessmentTab } from "../types";
import { SiteReportLoadingState } from "../SiteReportContent";
import { useReportLoadingState } from "../useReportLoadingState";

export interface ErrorHandlingStrategy {
  handleReportLoaded: (
    hasLoaded: boolean,
    loadingState: SiteReportLoadingState,
    assessmentState: Record<string, Record<string, boolean>>,
    assessmentTabs: AssessmentTab[],
    toggleAssessmentState: (params: { tabKey: string; layerId?: string }) => void
  ) => void;
}

/**
 * Hook that manages error handling for the site report loading process.
 *
 * This hook automatically deselects layers that fail to load, preventing broken
 * UI states. It handles different strategies for expandable vs non-expandable tabs:
 * - Expandable tabs: Individual failed layers are deselected
 * - Non-expandable tabs: The entire tab is deselected if any layer fails
 *
 * Use cases:
 * - Gracefully handling API failures during layer data loading
 * - Preventing users from seeing error states for selected layers
 * - Automatically cleaning up the UI when layers fail to load
 * - Providing feedback about which layers couldn't be loaded
 *
 * @returns Object containing the loading state and error handler function
 */
export function useReportErrorHandler() {
  const reportLoadingState = useReportLoadingState();
  const { setReportLoadingState } = reportLoadingState;

  const handleReportLoaded = useCallback(
    (
      _hasLoaded: boolean,
      loadingState: SiteReportLoadingState,
      assessmentState: Record<string, Record<string, boolean>>,
      assessmentTabs: AssessmentTab[],
      toggleAssessmentState: (params: { tabKey: string; layerId?: string }) => void
    ) => {
      setReportLoadingState(loadingState);

      Object.entries(assessmentState).forEach(([tabKey, tabState]) => {
        const tabData = assessmentTabs.find((tab) => tab.key === tabKey);
        if (!tabData) {
          return;
        }

        if (tabData.expandable) {
          handleExpandableTabErrors(tabKey, tabState, loadingState, toggleAssessmentState);
        } else {
          handleNonExpandableTabErrors(tabKey, tabState, loadingState, toggleAssessmentState);
        }
      });
    },
    [setReportLoadingState]
  );

  return {
    reportLoadingState,
    handleReportLoaded,
  };
}

/**
 * Handles errors for expandable tabs by deselecting individual failed layers.
 *
 * For expandable tabs, users can see and interact with individual layers,
 * so we only deselect the specific layers that failed to load.
 *
 * @param tabKey - The key of the tab containing the layers
 * @param tabState - Object mapping layer IDs to their selection state
 * @param loadingState - Current loading state for all layers
 * @param toggleAssessmentState - Function to toggle layer selection
 */
function handleExpandableTabErrors(
  tabKey: string,
  tabState: Record<string, boolean>,
  loadingState: SiteReportLoadingState,
  toggleAssessmentState: (params: { tabKey: string; layerId?: string }) => void
) {
  Object.entries(tabState).forEach(([layerId, isLayerSelected]) => {
    const layerLoadingState = loadingState[layerId];
    if (isLayerSelected && layerLoadingState?.mounted && !layerLoadingState.loading && layerLoadingState.error) {
      toggleAssessmentState({ tabKey, layerId });
    }
  });
}

/**
 * Handles errors for non-expandable tabs by deselecting the entire tab if any layer fails.
 *
 * For non-expandable tabs, users can only select/deselect the entire tab,
 * so if any layer within the tab fails, we deselect the whole tab to maintain
 * consistency.
 *
 * @param tabKey - The key of the tab containing the layers
 * @param tabState - Object mapping layer IDs to their selection state
 * @param loadingState - Current loading state for all layers
 * @param toggleAssessmentState - Function to toggle tab selection
 */
function handleNonExpandableTabErrors(
  tabKey: string,
  tabState: Record<string, boolean>,
  loadingState: SiteReportLoadingState,
  toggleAssessmentState: (params: { tabKey: string; layerId?: string }) => void
) {
  const hasAnyError = Object.entries(tabState).some(([layerId, isLayerSelected]) => {
    const layerLoadingState = loadingState[layerId];
    return isLayerSelected && layerLoadingState?.mounted && !layerLoadingState.loading && layerLoadingState.error;
  });

  if (hasAnyError) {
    toggleAssessmentState({ tabKey });
  }
}
